import { formatDate, sortByFieldIgnoreCase } from '../../../lib/helpers';

import { ROUTE_CONFIG, DISCOVERY_ROUTE_CONFIG } from '../../../constants/routes';
import { isDiscoveryEnabled } from '../../../layout/sidebar/hooks/useNavigation';

// --- Project Details ---

const calculateProjectWeeks = (startDate, endDate) => {
    if (!startDate || !endDate) 
        return { current: null, total: null };
    
    const start = new Date(startDate);
    const end = new Date(endDate);
    const now = new Date();
    const msPerWeek = 7 * 24 * 60 * 60 * 1000;
    const total = Math.max(1, Math.ceil((end - start) / msPerWeek));
    const elapsed = now < start ? 0 : Math.ceil((now - start) / msPerWeek);
    const current = Math.min(Math.max(1, elapsed), total);
    return { current, total };
};

const calculateEntitlements = (entitlementsData) => {
    const entitlementBlocks = entitlementsData?.content;
    if (!entitlementBlocks || entitlementBlocks.length === 0) {
        return { available: 0, total: 0 };
    }

    const total = entitlementBlocks.reduce((sum, ent) => sum + (ent.total !== -1 ? ent.total : 0), 0);
    const used = entitlementBlocks.reduce((sum, ent) => sum + (ent.used !== -1 ? ent.used : 0), 0);
    return { available: total - used, total };
};

export const parseProjectDetailsResponse = ({ currentOrg, newMigrationData }) => {
    const { current: currentWeek, total: totalWeeks } = calculateProjectWeeks(
        currentOrg.project_start_date,
        currentOrg.project_end_date,
    );
    const { available: availableEntitlements, total: totalEntitlements } = calculateEntitlements(
        newMigrationData?.entitlements?.data,
    );
    return {
        projectName: currentOrg.name,
        currentWeek,
        totalWeeks,
        status: currentOrg.project_state,
        startDate: formatDate(currentOrg.project_start_date),
        endDate: formatDate(currentOrg.project_end_date),
        owner: currentOrg.project_owner,
        costCenter: currentOrg.project_cost_center,
        availableEntitlements,
        totalEntitlements,
        isLoading: newMigrationData?.entitlements?.isLoading ?? false,
        isError: newMigrationData?.entitlements?.isError ?? false,
    };
};

export const projectDetailsCardValues = ({ isLoading, isError, ...parsed }, handlers) => ({
    ...parsed,
    queryState: { isLoading, isError },
    onManageClick: handlers?.handleCardClick ? () => handlers.handleCardClick(ROUTE_CONFIG.projects.path) : undefined,
});

// --- Sources ---

export const parseSourcesResponse = ({ newMigrationData, currentOrg }) => ({
    sources: newMigrationData?.sources?.count,
    isLoading: newMigrationData?.sources?.isLoading ?? false,
    isError: newMigrationData?.sources?.isError ?? false,
    isDiscoveryEnabled: isDiscoveryEnabled(currentOrg),
});


export const sourcesCardValues = ({ sources, isLoading, isError, isDiscoveryEnabled }, handlers) => ({
    sources,
    headerAction: isDiscoveryEnabled ? { label: 'Add More', href: DISCOVERY_ROUTE_CONFIG.inventory.path } : null,
    onClick: handlers?.handleCardClick ? () => handlers.handleCardClick(ROUTE_CONFIG.sources.path) : undefined,
    queryState: { isLoading, isError },
});

// --- Migration Status ---

export const parseMigrationStatusResponse = ({ newMigrationData }) => ({
    migrationData: newMigrationData?.migrationStatus,
});

export const migrationStatusCardValues = (parsed) => parsed;

// --- Migration Appliances ---

export const parseMigrationAppliancesResponse = ({ newMigrationData }) => ({
    appliances: sortByFieldIgnoreCase(newMigrationData?.appliances?.list ?? [], 'name'),
    isLoading: newMigrationData?.appliances?.isLoading ?? false,
    isError: newMigrationData?.appliances?.isError ?? false,
});

export const migrationAppliancesCardValues = ({ appliances, isLoading, isError }) => ({
    title: 'Migration Appliances',
    count: appliances.length,
    appliances,
    queryState: { isLoading, isError },
});
